import React, { useEffect, useLayoutEffect, useState } from "react";
import { ThemeProvider } from "styled-components";
import Helmet from "react-helmet";
import {
  Outlet,
  RouterProvider,
  ScrollRestoration,
  createBrowserRouter,
  useLocation,
} from "react-router-dom";

import "./App.css";
import { XObject } from "./XObject";
import { cmsData } from "./cmsData";
import { state } from "./state";
import { Header, HeaderShadow } from "./components/sections/Header";
import Home from "./pages/Home";
import Donate from "./pages/Donate";
import Stories from "./pages/Stories";
import Story from "./pages/Story";
import Timeline from "./pages/Timeline";
import Completion from "./pages/Completion";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import { Footer } from "./components/sections/Footer";
import { warmStories } from "./stories";
import { filterVisibleTitles } from "./visibility";
import RouteFocus from "./components/RouteFocus";
import SkipLink from "./components/SkipLink";
import RouteError, { NotFound } from "./components/RouteError";

XObject.observe(state, "showMenu", () => {
  document.body.classList.toggle("showMenu", state.showMenu);
});

// The page uses CSS smooth scrolling for in-page anchors, and the router's
// scroll restoration would pick that up too. A route change must jump, not
// glide under the page transition, so smooth scrolling is paused for the
// frame in which the router repositions the page. Rendered before
// ScrollRestoration so this layout effect runs first.
const InstantRouteScroll = () => {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    const html = document.documentElement;
    html.style.scrollBehavior = "auto";
    // Force the style change to take effect now. Without this flush the
    // scroll that follows in the same commit still uses the smooth value
    // and glides to the top underneath the page transition.
    void getComputedStyle(html).scrollBehavior;
    const frame = requestAnimationFrame(() => {
      html.style.scrollBehavior = "";
    });
    return () => cancelAnimationFrame(frame);
  }, [pathname]);
  return null;
};

// Header and footer live inside the router so their links navigate in place.
// ScrollRestoration scrolls to the top on each new page (or to the hash
// target when there is one) and puts the reader back where they were on back;
// RouteFocus then moves keyboard focus there. The default title takes over
// again whenever a page that set its own title (a story) unmounts.
const Chrome = ({ children }: { children: React.ReactNode }) => (
  <>
    <Helmet defaultTitle="SignUp Media" />
    <Header />
    <HeaderShadow />
    <main id="content" tabIndex={-1}>
      {children}
    </main>
    <Footer />
    <InstantRouteScroll />
    <ScrollRestoration />
    <RouteFocus />
    <SkipLink />
  </>
);

// The tab title, and what a screen reader announces on arrival, per page.
// Home keeps the default; a story and the error pages set their own.
const Page = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <>
    <Helmet>
      <title>SignUp Media - {title}</title>
    </Helmet>
    {children}
  </>
);

const Layout = () => (
  <Chrome>
    <Outlet />
  </Chrome>
);

const pages = [
  {
    path: "/",
    element: <Home />,
  },
  {
    path: "/donate",
    element: (
      <Page title="Donate">
        <Donate />
      </Page>
    ),
  },
  {
    path: "/completion",
    element: (
      <Page title="Thank you">
        <Completion />
      </Page>
    ),
  },
  {
    path: "/stories",
    element: (
      <Page title="Stories">
        <Stories />
      </Page>
    ),
  },
  {
    path: "/story/:id",
    element: <Story />,
  },
  {
    path: "/timeline",
    element: (
      <Page title="Timeline">
        <Timeline />
      </Page>
    ),
  },
  {
    path: "/privacy-policy",
    element: (
      <Page title="Privacy Policy">
        <PrivacyPolicy />
      </Page>
    ),
  },
  {
    path: "/terms-and-conditions",
    element: (
      <Page title="Terms and Conditions">
        <TermsAndConditions />
      </Page>
    ),
  },
  {
    path: "*",
    element: <NotFound />,
  },
];

const router = createBrowserRouter([
  {
    element: <Layout />,
    // A page that throws still gets the site chrome around the message.
    errorElement: (
      <Chrome>
        <RouteError />
      </Chrome>
    ),
    children: pages,
  },
]);

function App() {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    document.body.classList.add(localStorage.getItem("theme") || "light");
    (async () => {
      const [data, cms] = await Promise.all([
        (
          await fetch(
            "https://signup-extension-data.s3.amazonaws.com/data.json"
          )
        ).json(),
        (
          await fetch("https://signup-extension-data.s3.amazonaws.com/cms.json")
        ).json(),
      ]);
      setData(filterVisibleTitles(data));

      for (const key in cms) {
        cmsData[key] = cms[key];
      }
      setLoaded(true);

      // Once the browser is idle, fetch the stories list and the image of
      // the newest story so the Stories page arrives fully painted.
      const whenIdle = (fn: () => void) =>
        typeof window.requestIdleCallback === "function"
          ? window.requestIdleCallback(fn)
          : window.setTimeout(fn, 1500);
      whenIdle(() => {
        warmStories();
      });

      if (window.location.hostname !== "localhost") {
        // Adds signly after content has loaded
        const SIGNLY_SCRIPT =
          "https://cdn.signly.co/release/latest/signly-dynamic-american.umd.min.js";
        const script = document.createElement("script");
        script.src = SIGNLY_SCRIPT;
        document.body.append(script);
      }
    })();
  }, []);

  useEffect(() => {
    if (loaded) {
      setTimeout(() => {
        const hash = window.location.hash;
        if (hash) {
          // scroll to id element
          const id = hash.substr(1);
          const el = document.getElementById(id);
          if (el) {
            el.scrollIntoView();
          }
        }
      }, 500);
    }
  }, [loaded]);

  if (!loaded) return;
  return (
    <>
      <ThemeProvider
        theme={{
          cms: cmsData,
          data,
          loaded,
        }}
      >
        <RouterProvider router={router} />
      </ThemeProvider>
    </>
  );
}

export default App;
